/**
 * Robust canonicalization for institutional matching.
 * Handles punctuation, suffixes, and whitespace variations to create a unique matching key.
 */
export const normalizeOrg = (s: string) =>
  s?.normalize("NFKD")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[’'`]/g, "")                  // apostrophes
    .replace(/[^a-z0-9\s]/g, " ")           // punctuation to space
    .replace(/\b(the|inc|llc|ltd|co|corp|corporation|company)\b/g, "") // remove common suffixes
    .replace(/\s+/g, "")                    // collapse all spaces for strict matching
    .trim() || "";

export const formatPhoneNumber = (phone: string | null) => {
  if (!phone) return '—';
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return phone;
};

/**
 * Formats a date string for display without timezone offset issues.
 * Treats the input as a "Calendar Date" by using UTC components.
 */
export const formatDateSafe = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    
    // Use UTC components to ensure the date doesn't shift based on local timezone
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    
    return new Date(year, month, day).toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch (e) {
    return '—';
  }
};

/**
 * Formats a date string for display with weekday.
 */
export const formatFullDateSafe = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    
    return new Date(year, month, day).toLocaleDateString(undefined, { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch (e) {
    return '—';
  }
};
